'use strict'

/**
 * Settlement-dialog form-state hydration + validator + settled-ETA helpers.
 *
 * Answers: "how do I hydrate, validate, and submit the settlement dialog's
 * form state?"
 *
 * Sections (top → bottom):
 *   - settled-ETA derivation (business-day arithmetic)
 *   - ad-hoc-fees + disposition enums + cash-invoice predicate
 *   - settlement-input validator (mock-api write boundary mirror)
 *   - dialog seed helpers (blank row, today, ad-hoc fee id factory)
 *   - hydrateSettlementFormState (create + edit modes)
 *   - buildSettlementSubmitPayload (RAW inputs only — totals recomputed on receive)
 *   - assay-fee + other-fees hydration helpers
 *
 * Pure helpers powering MasterLotSettlementDialog. Extracted from the SFC
 * so each path can be unit tested without mounting Vue, jsdom, or vuetify
 * stubs.
 *
 * Mental model:
 *   - the dialog declares reactive form state (reportedGrossToz, perMetal,
 *     contractFees, assayOptIn, otherFeeIncluded, adHocFees, …)
 *   - these helpers compute fresh state slices from the masterLot prop
 *     (hydrate side) or from current form state (submit side)
 *   - the dialog method calls assign the returned slice into its reactive
 *     data — single-assignment pattern keeps Vue 2 reactivity intact
 *     without `$set` because every key is pre-declared in `data()`
 *
 * No `this`, no `$emit`, no DOM — runs in isolation.
 */

import {DateTime} from 'luxon-business-days'
import {prefixedId} from '@/util/prefixedId'
import {DISPOSITION_MODES, SETTLEMENT_DIALOG_MODES} from '../constants'
import {resolveAssayFeeFromContract} from '../upstreamContract'
import {buildManifestSummaryForVariance, computeSettlementPreview} from './math'

// ============================================================================
// Settled-ETA derivation
// ----------------------------------------------------------------------------
// Derive the `settled` milestone ETA from a `received.occurredAt` anchor +
// partner SLA business days.
// ============================================================================

export const settlementEtaFromReceived = (receivedOccurredAtIso, businessDays) => {
  if (!receivedOccurredAtIso) return null
  const days = Number(businessDays)
  if (!Number.isFinite(days) || days < 0) return null
  const anchor = DateTime.fromISO(receivedOccurredAtIso)
  if (!anchor.isValid) return null
  return anchor.plusBusiness({days}).toISO()
}

/**
 * Return a copy of `masterLot` with the `settled` milestone's `expectedAt`
 * replaced by `expectedAt`. No-op (returns the input lot) when `expectedAt`
 * is falsy or the lot has no `settled` milestone — keeps callers terse:
 *
 *     return applySettledEta(lot, settlementEtaFromReceived(receivedAt, days))
 *
 * Pure: never mutates the input lot or its milestones array.
 */
export const applySettledEta = (masterLot, expectedAt) => {
  if (!expectedAt) return masterLot
  if (!masterLot || !Array.isArray(masterLot.milestones)) return masterLot
  if (!masterLot.milestones.some((milestone) => milestone && milestone.kind === 'settled')) {
    return masterLot
  }
  return {
    ...masterLot,
    milestones: masterLot.milestones.map((milestone) => (
      milestone && milestone.kind === 'settled' ? {...milestone, expectedAt} : milestone
    )),
  }
}

// ============================================================================
// Ad-hoc fees enum + disposition enums
// ----------------------------------------------------------------------------
// Settlement-side enum for categorized refining-statement line items so
// slaModifiers can detect impurity charges without label-sniffing.
// Pre-extension rows hydrate with code:null and safely fail the
// impurity-presence modifier. Distinct from the contract-side
// otherFees[].code enum in upstreamContract.
// ============================================================================

export const ALLOWED_AD_HOC_FEE_CODES = Object.freeze([
  'impurity_charge',
  'wire',
  'treatment_overage',
  'sample_return',
  'copper_addition',
  'other',
])

export const SETTLEMENT_DISPOSITION_MODES = Object.freeze(Object.values(DISPOSITION_MODES))

export const isCashInvoiceDisposition = (dispositionMode) => (
  !dispositionMode || dispositionMode === DISPOSITION_MODES.CASH_INVOICE
)

// ============================================================================
// Settlement-input validator
// ============================================================================

/**
 * Validate settlement input. Per-metal rows require purityPct>0 +
 * finite non-negative metalValueUsd (exclusion derived from
 * computeSettlementPreview). adHocFees: non-empty label + finite
 * signed amountUsd (negatives = credits/rebates per partner
 * statement). dispositionMode optional; absent → 'cash_invoice'
 * downstream.
 */
export const validateSettlementInput = (masterLot, input) => {
  const errors = []
  if (!input || typeof input !== 'object') return ['settlement payload required']
  if (!(input.reportedGrossToz > 0)) {
    errors.push('reportedGrossToz must be positive')
  }
  const paidMetals = Array.isArray(masterLot.contract?.paidMetals) ? masterLot.contract.paidMetals : []
  const perMetal = input.perMetal || {}
  const previewByMetal = computeSettlementPreview(masterLot.contract, buildManifestSummaryForVariance(masterLot), {
    reportedGrossToz: input.reportedGrossToz,
    perMetal,
  })
  for (const metal of paidMetals) {
    const metalKey = String(metal).toLowerCase()
    const previewRow = previewByMetal[metalKey] || {}
    if (previewRow.excludedFromContract) continue
    const row = perMetal[metalKey] || {}
    if (!(row.purityPct > 0)) {
      errors.push(`purity required for ${metal}`)
    }
    if (row.metalValueUsd === null || row.metalValueUsd === undefined) {
      errors.push(`metalValueUsd required for ${metal}`)
    } else if (typeof row.metalValueUsd !== 'number' || !Number.isFinite(row.metalValueUsd)) {
      errors.push(`metalValueUsd must be a finite number for ${metal}`)
    } else if (row.metalValueUsd < 0) {
      errors.push(`metalValueUsd must be >= 0 (not negative) for ${metal}`)
    }
  }
  if (Array.isArray(input.adHocFees)) {
    input.adHocFees.forEach((row, idx) => {
      if (!row || typeof row.label !== 'string' || row.label.trim() === '') {
        errors.push(`ad-hoc fee row ${idx + 1} requires a non-empty label`)
      }
      if (typeof row?.amountUsd !== 'number' || !Number.isFinite(row.amountUsd)) {
        errors.push(`ad-hoc fee row ${idx + 1} requires a finite amountUsd`)
      }
      if (row && row.code !== undefined && row.code !== null) {
        if (!ALLOWED_AD_HOC_FEE_CODES.includes(row.code)) {
          errors.push(`ad-hoc fee row ${idx + 1} code must be one of: ${ALLOWED_AD_HOC_FEE_CODES.join(', ')}`)
        }
      }
    })
  }
  if (input.dispositionMode !== undefined && input.dispositionMode !== null) {
    if (!SETTLEMENT_DISPOSITION_MODES.includes(input.dispositionMode)) {
      errors.push(`dispositionMode must be one of: ${SETTLEMENT_DISPOSITION_MODES.join(', ')}`)
    }
  }
  return errors
}

// ============================================================================
// Dialog seed helpers
// ============================================================================

export const blankSettlementPerMetalRow = () => ({purityPct: null, metalValueUsd: null})

export const todaySettlementIsoDate = () => new Date().toISOString().slice(0, 10)

export const generateAdHocFeeId = () => prefixedId('fee')

// ============================================================================
// Hydrate dialog form-state slice
// ============================================================================

/**
 * Compute the entire hydrated form-state slice from a master lot + mode.
 * Returns the union of fields the dialog's `data()` declares so the caller
 * can fan it out via `Object.assign(this, slice)`.
 *
 * For `mode === 'edit'`, projects `settlement.*` back into form fields;
 * otherwise produces a blank state seeded from the contract.
 *
 * @param {{masterLot, mode, paidMetals, contract, contractOtherFees, manifestGrossToz}} options
 */
export const hydrateSettlementFormState = ({
  masterLot,
  mode,
  paidMetals,
  contract,
  contractOtherFees,
  manifestGrossToz,
}) => {
  const settlement = masterLot && masterLot.settlement
  if (mode === SETTLEMENT_DIALOG_MODES.EDIT && settlement) {
    const assay = hydrateAssayFeesFromSettlement(contract, settlement, paidMetals)
    const other = hydrateOtherFeesFromSettlement(contractOtherFees, settlement)
    return {
      reportedGrossToz: settlement.reportedGrossToz ?? manifestGrossToz,
      partnerReferenceNumber: settlement.partnerReferenceNumber || '',
      recordedAt: (settlement.recordedAt || '').slice(0, 10) || todaySettlementIsoDate(),
      notes: settlement.notes || '',
      documents: Array.isArray(settlement.documents)
        ? settlement.documents.map((doc) => ({...doc}))
        : [],
      perMetal: hydratePerMetalFromSettlement(settlement, paidMetals),
      contractFees: {perMetalAssayFee: assay.perMetalAssayFee, otherFees: []},
      assayOptIn: assay.assayOptIn,
      otherFeeIncluded: other.otherFeeIncluded,
      adHocFees: hydrateAdHocFeesFromSettlement(settlement),
      dispositionMode: SETTLEMENT_DISPOSITION_MODES.includes(settlement.dispositionMode)
        ? settlement.dispositionMode
        : DISPOSITION_MODES.CASH_INVOICE,
      dispositionDetails: settlement.dispositionDetails && typeof settlement.dispositionDetails === 'object'
        ? {...settlement.dispositionDetails}
        : {destination: '', note: ''},
    }
  }
  const assay = hydrateAssayFeesFromContract(contract, paidMetals)
  const other = hydrateOtherFeesFromContract(contractOtherFees)
  return {
    reportedGrossToz: manifestGrossToz || null,
    partnerReferenceNumber: '',
    recordedAt: todaySettlementIsoDate(),
    notes: '',
    documents: [],
    perMetal: paidMetals.reduce((acc, m) => {
      acc[m] = blankSettlementPerMetalRow()
      return acc
    }, {}),
    contractFees: {perMetalAssayFee: assay.perMetalAssayFee, otherFees: []},
    assayOptIn: assay.assayOptIn,
    otherFeeIncluded: other.otherFeeIncluded,
    adHocFees: [],
    // cash_invoice is the no-op default; non-cash modes reveal
    // per-metal credited-fine-toz + dispositionDetails block.
    dispositionMode: DISPOSITION_MODES.CASH_INVOICE,
    dispositionDetails: {destination: '', note: ''},
  }
}

// ============================================================================
// Build submit payload
// ============================================================================

/**
 * Build the wire payload emitted on submit. Carries RAW operator inputs
 * only — derived fields (creditedFineToz, refiningFeeUsd, totals) get
 * recomputed by the api/backend on receive so we never persist stale
 * derivations. Skips excluded metals (operator never typed an override).
 *
 * @param {{state, paidMetals, settlementContractFees, previewByMetal}} options
 */
export const buildSettlementSubmitPayload = ({
  state,
  paidMetals,
  settlementContractFees,
  previewByMetal,
}) => {
  const perMetalPayload = {}
  for (const metal of paidMetals) {
    const previewRow = previewByMetal[String(metal).toLowerCase()] || {}
    if (previewRow.excludedFromContract) continue
    const inputRow = state.perMetal[metal]
    perMetalPayload[metal] = {
      purityPct: Number(inputRow.purityPct),
      metalValueUsd: Number(inputRow.metalValueUsd),
    }
  }
  const adHocFeesPayload = state.adHocFees.map((row) => {
    const payloadRow = {
      id: row.id,
      label: typeof row.label === 'string' ? row.label.trim() : '',
      amountUsd: Number(row.amountUsd),
      // Null when admin hasn't categorized; otherwise the enum value
      // the dropdown emits.
      code: row.code || null,
    }
    if (row.note) payloadRow.note = row.note
    return payloadRow
  })
  const recordedAtIso = state.recordedAt
    ? new Date(`${state.recordedAt}T12:00:00.000Z`).toISOString()
    : new Date().toISOString()
  // cash_invoice is no-op default; field is always carried so legacy
  // hydration doesn't have to dance around its absence.
  const dispositionMode = isCashInvoiceDisposition(state.dispositionMode)
    ? DISPOSITION_MODES.CASH_INVOICE
    : state.dispositionMode
  const trimmedDestination = typeof state.dispositionDetails?.destination === 'string'
    ? state.dispositionDetails.destination.trim()
    : ''
  const trimmedDetailsNote = typeof state.dispositionDetails?.note === 'string'
    ? state.dispositionDetails.note.trim()
    : ''
  const dispositionDetails = (trimmedDestination || trimmedDetailsNote)
    ? {
      ...(trimmedDestination ? {destination: trimmedDestination} : {}),
      ...(trimmedDetailsNote ? {note: trimmedDetailsNote} : {}),
    }
    : null
  return {
    recordedAt: recordedAtIso,
    partnerReferenceNumber: state.partnerReferenceNumber,
    reportedGrossToz: Number(state.reportedGrossToz),
    perMetal: perMetalPayload,
    contractFees: settlementContractFees,
    assayOptIn: {...state.assayOptIn},
    otherFeeIncluded: {...state.otherFeeIncluded},
    adHocFees: adHocFeesPayload,
    notes: state.notes,
    documents: state.documents.map((doc) => ({...doc})),
    dispositionMode,
    dispositionDetails,
  }
}

export const buildSettlementContractFees = ({
  metalRows,
  assayOptIn,
  otherFees,
  otherFeeIncluded,
}) => ({
  perMetalAssayFee: Object.fromEntries((metalRows || []).map((row) => {
    const entry = {feeUsd: row.feeUsd}
    if (row.excluded === true) {
      entry.optedIn = false
    } else if (row.requiresOptIn === true) {
      entry.optedIn = assayOptIn?.[row.metal] === true
    }
    return [String(row.metal).toLowerCase(), entry]
  })),
  otherFees: (otherFees || []).map((fee) => ({
    ...fee,
    included: fee.conditional === true ? otherFeeIncluded?.[fee.code] === true : true,
  })),
})

// ============================================================================
// Assay-fee + other-fees hydration helpers
// ============================================================================

/**
 * Per-metal assay-fee + opt-in slice seeded from the contract's `assayFee`
 * map (or the legacy flat `assayFeeUsd` fallback handled by
 * `resolveAssayFeeFromContract`). For create mode and as the
 * "before-persisted-overlay" baseline in edit mode.
 *
 *   { perMetalAssayFee: { [metalKey]: { feeUsd } },
 *     assayOptIn:       { [metal]: false } }
 */
export const hydrateAssayFeesFromContract = (contract, paidMetals) => {
  const contractAssay = resolveAssayFeeFromContract(contract)
  const perMetalAssayFee = {}
  const assayOptIn = {}
  for (const metal of paidMetals) {
    const key = String(metal).toLowerCase()
    const row = contractAssay[key] || {feeUsd: 0}
    perMetalAssayFee[key] = {feeUsd: row.feeUsd, contractDefault: true}
    if (row.optInAtSalesOrder) assayOptIn[metal] = false
  }
  return {perMetalAssayFee, assayOptIn}
}

/**
 * Per-metal assay-fee + opt-in slice projected back from a persisted
 * `settlement.contractFees`. Precedence:
 *   1. persisted `perMetalAssayFee[key].feeUsd` (operator-edited at submit)
 *   2. legacy flat `assayFeeUsd` when only one paid metal
 *   3. contract default (`resolveAssayFeeFromContract`)
 */
export const hydrateAssayFeesFromSettlement = (contract, settlement, paidMetals) => {
  const contractAssay = resolveAssayFeeFromContract(contract)
  const persisted = settlement.contractFees?.perMetalAssayFee || {}
  const legacyAssayUsd = settlement.contractFees?.assayFeeUsd
  const perMetalAssayFee = {}
  const assayOptIn = {}
  for (const metal of paidMetals) {
    const key = String(metal).toLowerCase()
    const contractRow = contractAssay[key] || {feeUsd: 0}
    const persistedRow = persisted[key]
    let feeUsd = contractRow.feeUsd
    if (persistedRow && typeof persistedRow.feeUsd === 'number') feeUsd = persistedRow.feeUsd
    else if (typeof legacyAssayUsd === 'number' && paidMetals.length === 1) feeUsd = legacyAssayUsd
    perMetalAssayFee[key] = {
      feeUsd,
      contractDefault: !(persistedRow || (typeof legacyAssayUsd === 'number' && paidMetals.length === 1)),
    }
    if (contractRow.optInAtSalesOrder) {
      assayOptIn[metal] = persistedRow?.optedIn === true
    }
  }
  return {perMetalAssayFee, assayOptIn}
}

/**
 * Conditional `otherFees` checkbox state seeded for create mode — every
 * conditional contract fee starts unchecked; non-conditional fees aren't
 * checkbox-driven (they're always included) and stay out of the map.
 */
export const hydrateOtherFeesFromContract = (contractOtherFees) => {
  const otherFeeIncluded = {}
  for (const fee of contractOtherFees) {
    if (fee.conditional) otherFeeIncluded[fee.code] = false
  }
  return {otherFeeIncluded}
}

/**
 * Conditional `otherFees` checkbox state projected back from the persisted
 * settlement. Each conditional contract fee's checked state mirrors the
 * stored `included` flag for that code.
 */
export const hydrateOtherFeesFromSettlement = (contractOtherFees, settlement) => {
  const otherFeeIncluded = {}
  const persisted = Array.isArray(settlement.contractFees?.otherFees)
    ? settlement.contractFees.otherFees
    : []
  const byCode = {}
  for (const row of persisted) {
    if (row && row.code) byCode[row.code] = row
  }
  for (const fee of contractOtherFees) {
    if (!fee.conditional) continue
    otherFeeIncluded[fee.code] = byCode[fee.code]?.included === true
  }
  return {otherFeeIncluded}
}

const hydratePerMetalFromSettlement = (settlement, paidMetals) => {
  const perMetal = {}
  for (const metal of paidMetals) {
    const existing = settlement.perMetal && settlement.perMetal[metal]
    perMetal[metal] = existing
      ? {
          purityPct: existing.purityPct ?? null,
          metalValueUsd: existing.metalValueUsd ?? null,
        }
      : blankSettlementPerMetalRow()
  }
  return perMetal
}

const hydrateAdHocFeesFromSettlement = (settlement) => {
  if (!Array.isArray(settlement.adHocFees)) return []
  return settlement.adHocFees.map((row) => ({
    id: row.id || generateAdHocFeeId(),
    label: row.label || '',
    amountUsd: typeof row.amountUsd === 'number' ? row.amountUsd : null,
    // Null sentinel so pre-extension rows don't misfire the
    // impurity_charge modifier; admin can backfill via the Code
    // dropdown.
    code: row.code || null,
    ...(row.note ? {note: row.note} : {}),
  }))
}
